import * as dayjs from 'dayjs';

import Helper from '../../helpers/Helper';
import { getTimeZone } from '../../helpers/utils';
import {
    COLOUR_BACKUP_DIFFERENTIAL,
    COLOUR_BACKUP_FULL,
    COLOUR_BACKUP_LOG,
    INSTANCE_TYPE_SQLSERVER,
} from '../../component/Constants';
import BackupSummary from '../../types/backup/BackupSummary';
import BackupHistoryItem from '../../types/backup/BackupHistoryItem';

export type BackupKind = 'full' | 'differential' | 'log';

export const BACKUP_KINDS: BackupKind[] = ['full', 'differential', 'log'];

export const BACKUP_KIND_LABELS: Record<BackupKind, string> = {
    full: 'Full',
    differential: 'Differential',
    log: 'Log',
};

export const BACKUP_KIND_COLOURS: Record<BackupKind, string> = {
    full: COLOUR_BACKUP_FULL,
    differential: COLOUR_BACKUP_DIFFERENTIAL,
    log: COLOUR_BACKUP_LOG,
};

const BACKUP_TYPE_LABELS: Record<string, string> = {
    full: 'Full',
    differential: 'Differential',
    log: 'Log',
    file: 'File',
    file_differential: 'File differential',
    partial: 'Partial',
    partial_differential: 'Partial differential',
    // The error log does not say whether a failed data backup was a full or
    // a differential one.
    data: 'Full or differential',
};

// The retention of backup history in the repository is 31 days by default.
export const BACKUP_PERIODS = [
    { days: 1 / 24, label: 'Last hour' },
    { days: 1, label: 'Last 24 hours' },
    { days: 7, label: 'Last 7 days' },
    { days: 30, label: 'Last 30 days' },
];

export const DEFAULT_BACKUP_PERIOD_DAYS = 30;

// What the backup history chart plots as the height of each backup.
export type BackupChartMetric = 'duration' | 'size' | 'compressedsize';

export const BACKUP_CHART_METRICS: { value: BackupChartMetric; label: string }[] = [
    { value: 'duration', label: 'Duration' },
    { value: 'size', label: 'Size' },
    { value: 'compressedsize', label: 'Compressed size' },
];

export function backupTypeLabel(type: string): string {
    return BACKUP_TYPE_LABELS[type] || type;
}

// Which series a backup belongs to: file and partial backups are data backups
// like full and differential ones.
export function backupKind(type: string): BackupKind {
    if (type === 'log') {
        return 'log';
    }
    return type.endsWith('differential') ? 'differential' : 'full';
}

// Backups are timed to the second, so zero means under a second.
export function formatBackupDuration(milliseconds?: number | null): string {
    if (milliseconds === undefined || milliseconds === null) {
        return '-';
    }
    return milliseconds < 1000 ? '< 1s' : Helper.getTimeInEnglish(milliseconds);
}

// RPOs and intervals, to the minute.
export function formatInterval(seconds?: number | null): string {
    if (seconds === undefined || seconds === null) {
        return '-';
    }
    const minutes = Math.round(seconds / 60);
    return minutes < 1 ? '< 1m' : Helper.getTimeInEnglish(minutes * 60 * 1000);
}

export function formatBackupTime(value?: string | null): string {
    if (!value) {
        return '-';
    }
    return dayjs.default(value).tz(getTimeZone()).format('D MMM YYYY HH:mm');
}

export type BackupFlag = 'failed' | 'broken' | 'never' | 'nolog' | 'chainwarning' | null;

export const BACKUP_FLAG_TEXT: Record<Exclude<BackupFlag, null>, string> = {
    failed: 'A backup has failed since the last successful one',
    broken: 'The backup chain is broken',
    chainwarning: 'The backup chain has a warning',
    never: 'Never backed up',
    nolog: 'No log backups although the recovery model needs them',
};

// All current issues, most serious first. The leading icon uses the first
// issue, while its tooltip explains all of them.
export function backupFlags(row: BackupSummary): Exclude<BackupFlag, null>[] {
    const flags: Exclude<BackupFlag, null>[] = [];
    if (row.failures > 0) {
        flags.push('failed');
    }
    if (row.chainstatus === 'broken') {
        flags.push('broken');
    }
    if (!row.fullstart && !row.differentialstart) {
        flags.push('never');
    }
    if (row.logbackupsrequired && !row.logstart) {
        flags.push('nolog');
    }
    if (row.chainstatus === 'warning') {
        flags.push('chainwarning');
    }
    return flags;
}

export function backupFlag(row: BackupSummary): BackupFlag {
    return backupFlags(row)[0] ?? null;
}

export function backupWarningTooltip(row: BackupSummary): string {
    return backupFlags(row).map(flag => {
        // Use the chain's own explanation for warnings, matching its badge.
        if (flag === 'chainwarning') {
            return row.chainmessage || BACKUP_FLAG_TEXT[flag];
        }
        const detail = flag === 'failed' ? row.lastfailuremessage : flag === 'broken' ? row.chainmessage : undefined;
        return detail ? `${BACKUP_FLAG_TEXT[flag]}: ${detail}` : BACKUP_FLAG_TEXT[flag];
    }).join('\n');
}

// How many of the latest gaps between backups show the current schedule, so
// an older burst of manual backups does not outweigh it.
const RECENT_GAPS = 5;

// How often each kind of backup usually runs: the median gap, in seconds,
// between the latest consecutive successful backups of that kind. Copy-only
// backups are taken outside the schedule, so they are left out.
export function typicalIntervals(items: BackupHistoryItem[]): Partial<Record<BackupKind, number>> {
    const result: Partial<Record<BackupKind, number>> = {};

    for (const kind of BACKUP_KINDS) {
        const times = items
            .filter(item => item.status === 'success' && !item.iscopyonly && backupKind(item.backuptype) === kind)
            .map(item => Date.parse(item.timeslice))
            .filter(time => !isNaN(time))
            .sort((a, b) => a - b);
        const gaps = times.slice(1).map((time, i) => (time - times[i]) / 1000).slice(-RECENT_GAPS);

        if (gaps.length > 0) {
            gaps.sort((a, b) => a - b);
            const middle = Math.floor(gaps.length / 2);
            result[kind] = gaps.length % 2 ? gaps[middle] : (gaps[middle - 1] + gaps[middle]) / 2;
        }
    }

    return result;
}

// System databases per type, which the filter panel can exclude. tempdb is
// never listed, as it cannot be backed up.
const SYSTEM_DATABASES: Record<string, string[]> = {
    [INSTANCE_TYPE_SQLSERVER]: ['master', 'model', 'msdb'],
};

export function isSystemDatabase(row: BackupSummary): boolean {
    return (SYSTEM_DATABASES[row.type] ?? []).includes(row.database.toLowerCase());
}

// A stat card clicked to show only the databases it counts in the chain.
export type BackupCardFilter = 'backedup' | 'never' | 'attention' | null;

export const BACKUP_CARD_FILTER_LABELS: Record<Exclude<BackupCardFilter, null>, string> = {
    backedup: 'Backed up',
    never: 'Not backed up',
    attention: 'Needs attention',
};

// Same definitions as backupStats, so a card's count matches the rows it shows.
export function matchesCardFilter(row: BackupSummary, filter: BackupCardFilter): boolean {
    if (!filter) {
        return true;
    }
    const flags = backupFlags(row);
    switch (filter) {
        case 'backedup':
            return !flags.includes('never');
        case 'never':
            return flags.includes('never');
        default:
            return flags.length > 0;
    }
}

export interface BackupStats {
    databases: number;
    backedUp: number;
    notBackedUp: number;
    needsAttention: number;
}

// A database in the backup chain, with the copy of it on each monitored
// replica: primary first, then by instance name.
export type ChainRow = BackupSummary & { replicas: BackupSummary[] };

// An availability group database has a row per replica with the same key, and
// the same backups, since the server merges them. Show it once, as the
// primary's row like the backup alerts, or else the first replica's.
export function groupByDatabase(rows: BackupSummary[]): ChainRow[] {
    const groups = new Map<string, BackupSummary[]>();
    rows.forEach(row => {
        const group = groups.get(row.databasekey);
        if (group) {
            group.push(row);
        } else {
            groups.set(row.databasekey, [row]);
        }
    });

    const isPrimary = (row: BackupSummary) => row.replicarole === 'primary';
    return Array.from(groups.values()).map(group => {
        const replicas = [...group].sort((a, b) =>
            Number(isPrimary(b)) - Number(isPrimary(a)) || a.name.localeCompare(b.name));
        return { ...(group.find(isPrimary) ?? group[0]), replicas };
    });
}

// Totals for the cards above the backup chain, one per database.
export function backupStats(databases: ChainRow[]): BackupStats {
    const flags = databases.map(backupFlags);
    const notBackedUp = flags.filter(issues => issues.includes('never')).length;
    return {
        databases: databases.length,
        backedUp: databases.length - notBackedUp,
        notBackedUp,
        needsAttention: flags.filter(issues => issues.length > 0).length,
    };
}
